import * as t from 'io-ts';

import {makeEnumRuntimeType} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {RCardIdRef} from '../../../models/api/apiTypes';
import {RAccountDetailsState} from '../accounts/accountDetailsPanelState';
import {RCreateAccountFormState} from '../accounts/createAccountForm/createAccountFormState';
import {RContactDetailsPanelMergeState} from './contactDetailsPanelMergeState';

/*
 * Constants.
 */

export enum ContactDetailsSubStateTypesEnum {
  MERGE = 'MERGE_SUB_STATE',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS_SUB_STATE',
  CREATE_CONTACT = 'CREATE_CONTACT_SUB_STATE',
  CREATE_ACCOUNT = 'CREATE_ACCOUNT_SUB_STATE',
}
export const RContactDetailsSubStateType = makeEnumRuntimeType(ContactDetailsSubStateTypesEnum);

export enum ContactDetailsCreateAccountSourceActionsEnum {
  CONTACT_DETAILS = 'CONTACT_DETAILS',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
}
export const RContactDetailsCreateAccountSourceActions = makeEnumRuntimeType(
  ContactDetailsCreateAccountSourceActionsEnum,
);

/*
 * Base state.
 */

export const RContactDetailsPanelSubStateBaseDirty = t.type({
  type: RContactDetailsSubStateType,
});

/*
 * Sub-state Runtime types.
 */

export const RContactDetailsPanelMergeSubStateDirty = t.intersection([
  RContactDetailsPanelSubStateBaseDirty,
  RContactDetailsPanelMergeState,
  t.type({
    type: t.literal(ContactDetailsSubStateTypesEnum.MERGE),
  }),
]);

export const RContactDetailsPanelAccountDetailsSubStateDirty = t.intersection([
  RContactDetailsPanelSubStateBaseDirty,
  RAccountDetailsState,
  t.type({
    type: t.literal(ContactDetailsSubStateTypesEnum.ACCOUNT_DETAILS),
    isFetchRequired: t.boolean,
  }),
]);

export const RContactDetailsPanelCreateAccountSubStateDirty = t.intersection([
  RContactDetailsPanelSubStateBaseDirty,
  RCreateAccountFormState,
  t.type({
    type: t.literal(ContactDetailsSubStateTypesEnum.CREATE_ACCOUNT),
    source: RContactDetailsCreateAccountSourceActions,
    cardId: RCardIdRef,
  }),
]);

/*
 * Types.
 */

export interface ContactDetailsPanelMergeSubState
  extends Readonly<t.TypeOf<typeof RContactDetailsPanelMergeSubStateDirty>> {}
export const RContactDetailsPanelMergeSubState = t.clean<ContactDetailsPanelMergeSubState>(
  RContactDetailsPanelMergeSubStateDirty as any,
);

export interface ContactDetailsPanelAccountDetailsSubState
  extends Readonly<t.TypeOf<typeof RContactDetailsPanelAccountDetailsSubStateDirty>> {}
export const RContactDetailsPanelAccountDetailsSubState = t.clean<ContactDetailsPanelAccountDetailsSubState>(
  RContactDetailsPanelAccountDetailsSubStateDirty as any,
);

export interface ContactDetailsPanelCreateAccountSubState
  extends Readonly<t.TypeOf<typeof RContactDetailsPanelCreateAccountSubStateDirty>> {}
export const RContactDetailsPanelCreateAccountSubState = t.clean<ContactDetailsPanelCreateAccountSubState>(
  RContactDetailsPanelCreateAccountSubStateDirty as any,
);

const RContactDetailsPanelSubStateDirty = t.union([
  RContactDetailsPanelMergeSubState,
  RContactDetailsPanelAccountDetailsSubState,
  RContactDetailsPanelCreateAccountSubState,
]);
export type ContactDetailsPanelSubState =
  | ContactDetailsPanelMergeSubState
  | ContactDetailsPanelAccountDetailsSubState
  | ContactDetailsPanelCreateAccountSubState;
export const RContactDetailsPanelSubState = t.clean<ContactDetailsPanelSubState>(
  RContactDetailsPanelSubStateDirty as any,
);

/*
 * Type Guards.
 */

export function subStateIsMergeSubState(
  subState: ContactDetailsPanelSubState,
): subState is ContactDetailsPanelMergeSubState {
  return subState.type === ContactDetailsSubStateTypesEnum.MERGE;
}

export function subStateIsAccountDetailsSubState(
  subState: ContactDetailsPanelSubState,
): subState is ContactDetailsPanelAccountDetailsSubState {
  return subState.type === ContactDetailsSubStateTypesEnum.ACCOUNT_DETAILS;
}

export function subStateIsCreateAccountSubState(
  subState: ContactDetailsPanelSubState,
): subState is ContactDetailsPanelCreateAccountSubState {
  return subState.type === ContactDetailsSubStateTypesEnum.CREATE_ACCOUNT;
}
